import React, { useState, useEffect, useRef, useCallback } from 'react';
import { View, ScrollView, StyleSheet, Dimensions, TouchableOpacity } from 'react-native';
import { 
  Card, 
  Title, 
  Paragraph, 
  useTheme, 
  Button, 
  ActivityIndicator,
  Chip,
  Divider,
  SegmentedButtons,
  Surface
} from 'react-native-paper';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useRoute, useNavigation } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import type { RootStackParamList, CatchReport, GeoCoordinate } from '../types';
import { ERDDAPService } from '../services/erddapService';
import { logger } from '../utils/logger';
import { PlatformMap } from '../components/PlatformMap';

type CatchDetailRouteProp = RouteProp<RootStackParamList, 'CatchDetail'>;

const SCREEN_WIDTH = Dimensions.get('window').width;

export default function CatchDetailScreen() {
  const theme = useTheme();
  const navigation = useNavigation();
  const route = useRoute<CatchDetailRouteProp>();
  const { catchReport } = route.params;
  
  // Log the catch location for debugging
  logger.info('CatchDetailScreen - Catch location', {
    lat: catchReport.location.latitude,
    lng: catchReport.location.longitude,
    species: catchReport.species,
    timestamp: catchReport.timestamp
  });
  
  const [loading, setLoading] = useState(false);
  const [selectedOverlay, setSelectedOverlay] = useState<'sst' | 'sstLeo' | 'chlorophyll' | 'chlorophyllGap' | 'chlorophyllS3A' | 'chlorophyllS3B' | 'ssh' | 'currents'>('sst');
  const [overlayUrl, setOverlayUrl] = useState<string | null>(null);
  const [oceanData, setOceanData] = useState<any>(null);
  const [dataTimestamp, setDataTimestamp] = useState<string | null>(null);
  const [dataAvailable, setDataAvailable] = useState(true);
  const [calculatingDynamicRange, setCalculatingDynamicRange] = useState(false);
  const [currentZoom, setCurrentZoom] = useState(10);
  const [currentBounds, setCurrentBounds] = useState<any>(null);
  const [shouldReloadData, setShouldReloadData] = useState(false);
  const updateTimer = useRef<NodeJS.Timeout | null>(null);

  // Check if a dataset is available for the catch date
  const isDatasetAvailable = (dataset: string): boolean => {
    const catchDate = new Date(catchReport.timestamp);
    const now = new Date();
    const daysSinceCatch = (now.getTime() - catchDate.getTime()) / (1000 * 60 * 60 * 24);
    const useLatestData = daysSinceCatch < 3;
    
    if (useLatestData) return true; // All datasets available for recent catches
    
    switch (dataset) {
      case 'sstLeo':
        const sstLeoMinDate = new Date('2024-07-31T12:00:00Z');
        return catchDate >= sstLeoMinDate;
      case 'chlorophyll':
      case 'chlorophyllGap':
        const chlorophyllMinDate = new Date('2020-05-05');
        return catchDate >= chlorophyllMinDate;
      case 'chlorophyllS3A':
      case 'chlorophyllS3B':
        // Sentinel-3 data availability - assuming similar to regular chlorophyll
        const sentinel3MinDate = new Date('2020-05-05');
        return catchDate >= sentinel3MinDate;
      default:
        return true; // SST, SSH, and currents generally have longer history
    }
  };

  // Calculate bounds for ERDDAP data based on zoom level
  const getBounds = useCallback(() => {
    // Use current bounds if available (from map movement)
    if (currentBounds) {
      return currentBounds;
    }
    
    // Otherwise calculate based on zoom level
    const { height, width } = Dimensions.get('window');
    const aspectRatio = width / height;
    
    // Calculate the span of latitude/longitude visible on screen
    const latSpan = 180 / Math.pow(2, currentZoom - 1); // Adjusted for better coverage
    const lonSpan = latSpan * aspectRatio * 1.5; // Account for aspect ratio and add buffer
    
    const bounds = {
      minLat: Math.max(-90, catchReport.location.latitude - latSpan),
      maxLat: Math.min(90, catchReport.location.latitude + latSpan),
      minLon: Math.max(-180, catchReport.location.longitude - lonSpan),
      maxLon: Math.min(180, catchReport.location.longitude + lonSpan),
    };
    
    logger.info('Calculated bounds for overlay', { bounds, zoom: currentZoom });
    return bounds;
  }, [currentZoom, currentBounds, catchReport.location]);

  // Load oceanographic data for the catch date
  useEffect(() => {
    loadHistoricalData();
  }, [selectedOverlay]);
  
  // Reload data when shouldReloadData flag is set
  useEffect(() => {
    if (shouldReloadData) {
      loadHistoricalData();
      setShouldReloadData(false);
    }
  }, [shouldReloadData]);
  
  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (updateTimer.current) {
        clearTimeout(updateTimer.current);
      }
    };
  }, []);

  const loadHistoricalData = async () => {
    setLoading(true);
    setDataAvailable(true); // Reset data availability
    try {
      const bounds = getBounds();
      const catchDate = new Date(catchReport.timestamp);
      
      // Define minimum dates for data availability
      const chlorophyllMinDate = new Date('2020-05-05');
      const sstLeoMinDate = new Date('2024-07-31T12:00:00Z');
      
      // For very recent catches, use the latest available data instead
      const now = new Date();
      const daysSinceCatch = (now.getTime() - catchDate.getTime()) / (1000 * 60 * 60 * 24);
      const useLatestData = daysSinceCatch < 3; // Use latest if catch is within 3 days
      
      logger.info('Loading historical ocean data', { 
        overlay: selectedOverlay, 
        date: catchDate.toISOString(),
        daysSinceCatch,
        useLatestData,
        bounds 
      });

      // Get overlay URL with historical date or latest
      let url: string | null;
      
      switch (selectedOverlay) {
        case 'sst':
          try {
            if (!useLatestData && catchDate) {
              // For historical data, fetch the data to calculate dynamic range
              setCalculatingDynamicRange(true);
              const sstData = await ERDDAPService.getSST(bounds, 1.0, catchDate);
              const tempRange = ERDDAPService.calculateSSTRange(sstData);
              url = ERDDAPService.getImageOverlayURL('sst', bounds, { temperatureRange: tempRange, targetDate: catchDate });
              setCalculatingDynamicRange(false);
            } else {
              // Use dynamic SST for latest data
              setCalculatingDynamicRange(true);
              url = await ERDDAPService.getDynamicSSTOverlayURL(bounds, 'default');
              setCalculatingDynamicRange(false);
            }
          } catch (error) {
            logger.warn('Failed to get dynamic SST overlay, falling back to default', { error: error instanceof Error ? error.message : String(error) });
            setCalculatingDynamicRange(false);
            url = ERDDAPService.getImageOverlayURL('sst', bounds, useLatestData ? undefined : { targetDate: catchDate });
          }
          break;
        case 'sstLeo':
          // SST LEO dataset minimum date (July 31, 2024 at 12:00 UTC)
          const sstLeoMinDate = new Date('2024-07-31T12:00:00Z');
          
          // Check if catch date is before LEO data availability
          if (!useLatestData && catchDate < sstLeoMinDate) {
            logger.warn('SST LEO data not available for catch date', {
              catchDate: catchDate.toISOString(),
              minDate: sstLeoMinDate.toISOString()
            });
            // DO NOT load any data - data is not available for this date
            setDataAvailable(false);
            url = null;  // No overlay to show
          } else {
            try {
              if (!useLatestData) {
                // For historical data within LEO date range, fetch the data to calculate dynamic range
                setCalculatingDynamicRange(true);
                const sstData = await ERDDAPService.getSST(bounds, 1.0, catchDate, 'leo');
                const tempRange = ERDDAPService.calculateSSTRange(sstData);
                url = ERDDAPService.getImageOverlayURL('sst', bounds, { temperatureRange: tempRange, sstDataset: 'leo', targetDate: catchDate });
                setCalculatingDynamicRange(false);
              } else {
                // Use dynamic SST for latest data
                setCalculatingDynamicRange(true);
                url = await ERDDAPService.getDynamicSSTOverlayURL(bounds, 'leo');
                setCalculatingDynamicRange(false);
              }
            } catch (error) {
              logger.warn('Failed to get dynamic SST LEO overlay', { error: error instanceof Error ? error.message : String(error) });
              setCalculatingDynamicRange(false);
              // Don't fall back to latest data - show no data instead
              url = null;
              setDataAvailable(false);
            }
          }
          break;
        case 'chlorophyll':
          // Check if catch date is before May 2020 (chlorophyll data availability)
          if (!useLatestData && catchDate < chlorophyllMinDate) {
            logger.warn('Chlorophyll data not available for catch date', {
              catchDate: catchDate.toISOString(),
              minDate: chlorophyllMinDate.toISOString()
            });
            // DO NOT load any data - data is not available for this date
            setDataAvailable(false);
            url = null;  // No overlay to show
          } else {
            url = ERDDAPService.getImageOverlayURL('chlorophyll', bounds, useLatestData ? undefined : { targetDate: catchDate });
          }
          break;
        case 'chlorophyllGap':
          // GAP-filled chlorophyll dataset - same date restriction as regular chlorophyll
          if (!useLatestData && catchDate < chlorophyllMinDate) {
            logger.warn('Chlorophyll Gap data not available for catch date', {
              catchDate: catchDate.toISOString(),
              minDate: chlorophyllMinDate.toISOString()
            });
            setDataAvailable(false);
            url = null;
          } else {
            url = ERDDAPService.getImageOverlayURL('chlorophyll', bounds, { 
              chlorophyllDataset: 'gapFilled',
              targetDate: useLatestData ? undefined : catchDate 
            });
          }
          break;
        case 'chlorophyllS3A':
          // Sentinel-3A data - check availability
          if (!useLatestData && catchDate < chlorophyllMinDate) {
            logger.warn('Sentinel-3A data not available for catch date', {
              catchDate: catchDate.toISOString(),
              minDate: chlorophyllMinDate.toISOString()
            });
            setDataAvailable(false);
            url = null;
          } else {
            url = ERDDAPService.getImageOverlayURL('chlorophyllS3A', bounds, useLatestData ? undefined : { targetDate: catchDate });
          }
          break;
        case 'chlorophyllS3B':
          // Sentinel-3B data - check availability
          if (!useLatestData && catchDate < chlorophyllMinDate) {
            logger.warn('Sentinel-3B data not available for catch date', {
              catchDate: catchDate.toISOString(),
              minDate: chlorophyllMinDate.toISOString()
            });
            setDataAvailable(false);
            url = null;
          } else {
            url = ERDDAPService.getImageOverlayURL('chlorophyllS3B', bounds, useLatestData ? undefined : { targetDate: catchDate });
          }
          break;
        case 'ssh':
          url = ERDDAPService.getImageOverlayURL('ssh', bounds, useLatestData ? undefined : { targetDate: catchDate });
          break;
        case 'currents':
          url = ERDDAPService.getImageOverlayURL('currents', bounds, useLatestData ? undefined : { targetDate: catchDate });
          break;
      }
      
      setOverlayUrl(url);
      
      // Update data timestamp to reflect data availability
      if (!dataAvailable) {
        if (selectedOverlay === 'sstLeo') {
          setDataTimestamp('Data not available for this date (LEO available from July 31, 2024)');
        } else if (selectedOverlay === 'chlorophyll' || selectedOverlay === 'chlorophyllGap' || 
                   selectedOverlay === 'chlorophyllS3A' || selectedOverlay === 'chlorophyllS3B') {
          setDataTimestamp('Data not available for this date (available from May 5, 2020)');
        } else {
          setDataTimestamp('Data not available for this date');
        }
      } else {
        setDataTimestamp(useLatestData ? 'Latest available' : catchDate.toISOString());
      }
      
      // Also fetch point data for the exact location
      const pointBounds = {
        minLat: catchReport.location.latitude - 0.1,
        maxLat: catchReport.location.latitude + 0.1,
        minLon: catchReport.location.longitude - 0.1,
        maxLon: catchReport.location.longitude + 0.1,
      };
      
      let pointData: any;
      try {
        switch (selectedOverlay) {
          case 'sst':
            const sstData = await ERDDAPService.getSST(pointBounds, 0.1, useLatestData ? undefined : catchDate);
            if (sstData.length > 0) {
              const avgTemp = sstData.reduce((sum, d) => sum + d.temperature, 0) / sstData.length;
              pointData = {
                type: 'SST',
                value: avgTemp,
                unit: '°C',
                displayValue: `${avgTemp.toFixed(1)}°C (${((avgTemp * 9/5) + 32).toFixed(1)}°F)`
              };
            }
            break;
            
          case 'sstLeo':
            // For SST LEO, we need to fetch data from the LEO dataset
            // Since we can't modify the core service, we'll use the image overlay approach
            // to ensure we're getting LEO data
            try {
              // Use a small bounds area around the exact point
              const leoBounds = {
                minLat: catchReport.location.latitude - 0.05,
                maxLat: catchReport.location.latitude + 0.05,
                minLon: catchReport.location.longitude - 0.05,
                maxLon: catchReport.location.longitude + 0.05,
              };
              
              // For now, we'll display a message that LEO data point values are not available
              // The map overlay will still show the correct LEO data
              pointData = {
                type: 'SST (Low Orbit)',
                value: 0,
                unit: '°C',
                displayValue: 'LEO data shown on map'
              };
            } catch (error) {
              logger.warn('Failed to get SST LEO point data', { error: error instanceof Error ? error.message : String(error) });
            }
            break;
            
          case 'chlorophyll':
          case 'chlorophyllGap':
          case 'chlorophyllS3A':
          case 'chlorophyllS3B':
            // For chlorophyll, use appropriate date based on data availability
            let chloroDate = catchDate;
            if (selectedOverlay === 'chlorophyll' && !useLatestData && catchDate < chlorophyllMinDate) {
              chloroDate = chlorophyllMinDate;
            }
            // Determine the dataset variant
            let datasetVariant: 'default' | 'gapFilled' | 's3a' | 's3b' = 'default';
            if (selectedOverlay === 'chlorophyllGap') datasetVariant = 'gapFilled';
            else if (selectedOverlay === 'chlorophyllS3A') datasetVariant = 's3a';
            else if (selectedOverlay === 'chlorophyllS3B') datasetVariant = 's3b';
            
            const chloroData = await ERDDAPService.getChlorophyll(pointBounds, 0.1, useLatestData ? undefined : chloroDate, datasetVariant);
            if (chloroData.length > 0) {
              const avgConc = chloroData.reduce((sum, d) => sum + d.concentration, 0) / chloroData.length;
              let label = 'Chlorophyll (VIIRS)';
              if (selectedOverlay === 'chlorophyllGap') label = 'Chlorophyll (Gap-Filled)';
              else if (selectedOverlay === 'chlorophyllS3A') label = 'Chlorophyll (Sentinel-3A)';
              else if (selectedOverlay === 'chlorophyllS3B') label = 'Chlorophyll (Sentinel-3B)';
              pointData = {
                type: label,
                value: avgConc,
                unit: 'mg/m³',
                displayValue: `${avgConc.toFixed(2)} mg/m³`
              };
            }
            break;
            
          case 'ssh':
            const sshData = await ERDDAPService.getSSH(pointBounds, 0.1, useLatestData ? undefined : catchDate);
            if (sshData.length > 0) {
              const avgHeight = sshData.reduce((sum, d) => sum + d.height, 0) / sshData.length;
              pointData = {
                type: 'Sea Surface Height',
                value: avgHeight,
                unit: 'm',
                displayValue: `${avgHeight.toFixed(3)} m`
              };
            }
            break;
            
          case 'currents':
            const currentData = await ERDDAPService.getCurrents(pointBounds, 0.1, useLatestData ? undefined : catchDate);
            if (currentData.length > 0) {
              const avgSpeed = currentData.reduce((sum, d) => sum + d.speed, 0) / currentData.length;
              const avgDirection = currentData.reduce((sum, d) => sum + d.direction, 0) / currentData.length;
              pointData = {
                type: 'Current',
                value: avgSpeed,
                unit: 'm/s',
                displayValue: `${avgSpeed.toFixed(2)} m/s @ ${avgDirection.toFixed(0)}°`
              };
            }
            break;
        }
      } catch (error) {
        logger.warn('Failed to fetch point data, continuing without it', { error: error instanceof Error ? error.message : String(error) });
      }
      
      setOceanData(pointData);
      
    } catch (error) {
      logger.error('Error loading historical ocean data', error instanceof Error ? error : new Error(String(error)));
    } finally {
      setLoading(false);
    }
  };

  // Handle map moves with debounce
  const handleMapMove = useCallback((newCenter: { lat: number; lng: number }, newZoom: number, newBounds: any) => {
    setCurrentZoom(newZoom);
    setCurrentBounds(newBounds);
    
    // Clear any existing timer
    if (updateTimer.current) {
      clearTimeout(updateTimer.current);
    }
    
    // Update overlay after user stops moving (debounced)
    updateTimer.current = setTimeout(() => {
      logger.info('Updating overlay bounds after map movement', { 
        newBounds, 
        zoom: newZoom,
        selectedOverlay
      });
      setShouldReloadData(true);
    }, 1000); // 1 second debounce
  }, [selectedOverlay]);

  const formatDate = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      const options: Intl.DateTimeFormatOptions = {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      };
      return date.toLocaleDateString('en-US', options);
    } catch {
      return 'Unknown date';
    }
  };

  return (
    <ScrollView style={styles.container}>
      {/* Catch Information Card */}
      <Card style={styles.card}>
        <Card.Content>
          <View style={styles.header}>
            <MaterialCommunityIcons 
              name="fish" 
              size={32} 
              color={theme.colors.primary} 
            />
            <View style={styles.headerText}>
              <Title>{catchReport.species}</Title>
              <Paragraph>{formatDate(catchReport.timestamp)}</Paragraph>
            </View>
          </View>
          
          <View style={styles.details}>
            {catchReport.count > 1 && (
              <Chip icon="counter" style={styles.chip}>
                Count: {catchReport.count}
              </Chip>
            )}
            {catchReport.averageSize > 0 && (
              <Chip icon="weight" style={styles.chip}>
                {catchReport.averageSize} lbs
              </Chip>
            )}
            {catchReport.depth > 0 && (
              <Chip icon="arrow-down" style={styles.chip}>
                {catchReport.depth} ft
              </Chip>
            )}
            <Chip icon="map-marker" style={styles.chip}>
              {catchReport.location.latitude.toFixed(4)}°, {catchReport.location.longitude.toFixed(4)}°
            </Chip>
          </View>
          
          {catchReport.notes && (
            <>
              <Divider style={styles.divider} />
              <Paragraph style={styles.notes}>{catchReport.notes}</Paragraph>
            </>
          )}
        </Card.Content>
      </Card>

      {/* Ocean Data Selector */}
      <Card style={styles.card}>
        <Card.Content>
          <Title>Ocean Conditions at Catch</Title>
          <View style={styles.overlayButtonsContainer}>
            <View style={styles.overlayButtonRow}>
              <TouchableOpacity
                style={[styles.overlayButton, selectedOverlay === 'sst' && { backgroundColor: theme.colors.primaryContainer }]}
                onPress={() => setSelectedOverlay('sst')}
              >
                <Paragraph style={[styles.overlayButtonText, selectedOverlay === 'sst' && { color: theme.colors.primary }]}>
                  SST
                </Paragraph>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.overlayButton, 
                  selectedOverlay === 'sstLeo' && { backgroundColor: theme.colors.primaryContainer },
                  !isDatasetAvailable('sstLeo') && styles.overlayButtonDisabled
                ]}
                onPress={() => isDatasetAvailable('sstLeo') && setSelectedOverlay('sstLeo')}
                disabled={!isDatasetAvailable('sstLeo')}
              >
                <Paragraph style={[
                  styles.overlayButtonText, 
                  selectedOverlay === 'sstLeo' && { color: theme.colors.primary },
                  !isDatasetAvailable('sstLeo') && styles.overlayButtonTextDisabled
                ]}>
                  SST (LEO)
                </Paragraph>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.overlayButton, selectedOverlay === 'ssh' && { backgroundColor: theme.colors.primaryContainer }]}
                onPress={() => setSelectedOverlay('ssh')}
              >
                <Paragraph style={[styles.overlayButtonText, selectedOverlay === 'ssh' && { color: theme.colors.primary }]}>
                  SSH
                </Paragraph>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.overlayButton, selectedOverlay === 'currents' && { backgroundColor: theme.colors.primaryContainer }]}
                onPress={() => setSelectedOverlay('currents')}
              >
                <Paragraph style={[styles.overlayButtonText, selectedOverlay === 'currents' && { color: theme.colors.primary }]}>
                  Current
                </Paragraph>
              </TouchableOpacity>
            </View>
            <View style={styles.overlayButtonRow}>
              <TouchableOpacity
                style={[
                  styles.overlayButton, 
                  selectedOverlay === 'chlorophyll' && { backgroundColor: theme.colors.primaryContainer },
                  !isDatasetAvailable('chlorophyll') && styles.overlayButtonDisabled
                ]}
                onPress={() => isDatasetAvailable('chlorophyll') && setSelectedOverlay('chlorophyll')}
                disabled={!isDatasetAvailable('chlorophyll')}
              >
                <Paragraph style={[
                  styles.overlayButtonText, 
                  selectedOverlay === 'chlorophyll' && { color: theme.colors.primary },
                  !isDatasetAvailable('chlorophyll') && styles.overlayButtonTextDisabled
                ]}>
                  VIIRS
                </Paragraph>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.overlayButton, 
                  selectedOverlay === 'chlorophyllGap' && { backgroundColor: theme.colors.primaryContainer },
                  !isDatasetAvailable('chlorophyllGap') && styles.overlayButtonDisabled
                ]}
                onPress={() => isDatasetAvailable('chlorophyllGap') && setSelectedOverlay('chlorophyllGap')}
                disabled={!isDatasetAvailable('chlorophyllGap')}
              >
                <Paragraph style={[
                  styles.overlayButtonText, 
                  selectedOverlay === 'chlorophyllGap' && { color: theme.colors.primary },
                  !isDatasetAvailable('chlorophyllGap') && styles.overlayButtonTextDisabled
                ]}>
                  VIIRS Gap
                </Paragraph>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.overlayButton, 
                  selectedOverlay === 'chlorophyllS3A' && { backgroundColor: theme.colors.primaryContainer },
                  !isDatasetAvailable('chlorophyllS3A') && styles.overlayButtonDisabled
                ]}
                onPress={() => isDatasetAvailable('chlorophyllS3A') && setSelectedOverlay('chlorophyllS3A')}
                disabled={!isDatasetAvailable('chlorophyllS3A')}
              >
                <Paragraph style={[
                  styles.overlayButtonText, 
                  selectedOverlay === 'chlorophyllS3A' && { color: theme.colors.primary },
                  !isDatasetAvailable('chlorophyllS3A') && styles.overlayButtonTextDisabled
                ]}>
                  S3A
                </Paragraph>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.overlayButton, 
                  selectedOverlay === 'chlorophyllS3B' && { backgroundColor: theme.colors.primaryContainer },
                  !isDatasetAvailable('chlorophyllS3B') && styles.overlayButtonDisabled
                ]}
                onPress={() => isDatasetAvailable('chlorophyllS3B') && setSelectedOverlay('chlorophyllS3B')}
                disabled={!isDatasetAvailable('chlorophyllS3B')}
              >
                <Paragraph style={[
                  styles.overlayButtonText, 
                  selectedOverlay === 'chlorophyllS3B' && { color: theme.colors.primary },
                  !isDatasetAvailable('chlorophyllS3B') && styles.overlayButtonTextDisabled
                ]}>
                  S3B
                </Paragraph>
              </TouchableOpacity>
            </View>
          </View>
        </Card.Content>
      </Card>

      {/* Map with Overlay */}
      <Card style={styles.card}>
        <Card.Content>
          <Title>Location Map</Title>
          {dataTimestamp && (
            <Paragraph style={styles.timestamp}>
              Data from: {
                dataTimestamp === 'Latest available' || 
                dataTimestamp.includes('(earliest available)') 
                  ? dataTimestamp 
                  : formatDate(dataTimestamp)
              }
            </Paragraph>
          )}
          {(selectedOverlay === 'sst' || selectedOverlay === 'sstLeo') && dataAvailable && (
            <Paragraph style={styles.gradientNote}>
              Temperature edges are enhanced to highlight fishing hotspots
            </Paragraph>
          )}
          
          <View style={styles.mapContainer}>
            <PlatformMap
              center={{ lat: catchReport.location.latitude, lng: catchReport.location.longitude }}
              zoom={currentZoom}
              overlays={overlayUrl ? [{
                url: overlayUrl,
                bounds: getBounds(),
                type: 'image' as const
              }] : []}
              markers={[{
                position: { lat: catchReport.location.latitude, lng: catchReport.location.longitude },
                title: catchReport.species,
                icon: '🐟',
                size: [32, 32] as [number, number]
              }]}
              baseMapType="ocean"
              onMapMove={handleMapMove}
            />
            
            {(loading || calculatingDynamicRange) && (
              <View style={styles.loadingOverlay}>
                <ActivityIndicator size="large" color={theme.colors.primary} />
                <Paragraph>
                  {calculatingDynamicRange 
                    ? 'Calculating temperature gradients...' 
                    : 'Loading ocean data...'}
                </Paragraph>
              </View>
            )}
            
            {!loading && !calculatingDynamicRange && !dataAvailable && (
              <View style={styles.noDataOverlay}>
                <MaterialCommunityIcons 
                  name="alert-circle-outline" 
                  size={48} 
                  color={theme.colors.onSurfaceVariant} 
                />
                <Title style={styles.noDataTitle}>No Data Available</Title>
                <Paragraph style={styles.noDataText}>
                  {selectedOverlay === 'sstLeo' 
                    ? 'SST LEO data is only available from July 31, 2024 onwards'
                    : (selectedOverlay === 'chlorophyll' || selectedOverlay === 'chlorophyllGap' || 
                       selectedOverlay === 'chlorophyllS3A' || selectedOverlay === 'chlorophyllS3B')
                    ? 'Chlorophyll data is only available from May 5, 2020 onwards'
                    : 'Ocean data is not available for this date'}
                </Paragraph>
              </View>
            )}
          </View>
        </Card.Content>
      </Card>

      {/* Ocean Data Details */}
      {oceanData && (
        <Card style={styles.card}>
          <Card.Content>
            <Title>Ocean Conditions at Location</Title>
            <Surface style={styles.dataCard}>
              <MaterialCommunityIcons 
                name={
                  selectedOverlay === 'sst' || selectedOverlay === 'sstLeo' ? 'thermometer' :
                  selectedOverlay === 'chlorophyll' || selectedOverlay === 'chlorophyllGap' || selectedOverlay === 'chlorophyllS3A' || selectedOverlay === 'chlorophyllS3B' ? 'leaf' :
                  selectedOverlay === 'ssh' ? 'waves' :
                  'navigation'
                } 
                size={48} 
                color={theme.colors.primary} 
              />
              <View style={styles.dataText}>
                <Paragraph style={styles.dataLabel}>{oceanData.type}</Paragraph>
                <Title style={styles.dataValue}>{oceanData.displayValue}</Title>
              </View>
            </Surface>
            
            {catchReport.conditions && (
              <>
                <Divider style={styles.divider} />
                <Title style={styles.conditionsTitle}>Recorded Conditions</Title>
                <View style={styles.conditions}>
                  {catchReport.conditions.waterTemp > 0 && (
                    <Chip icon="thermometer" style={styles.conditionChip}>
                      Water: {catchReport.conditions.waterTemp}°F
                    </Chip>
                  )}
                  {catchReport.conditions.weather && (
                    <Chip icon="weather-partly-cloudy" style={styles.conditionChip}>
                      {catchReport.conditions.weather}
                    </Chip>
                  )}
                  {catchReport.conditions.windSpeed > 0 && (
                    <Chip icon="weather-windy" style={styles.conditionChip}>
                      Wind: {catchReport.conditions.windSpeed} kts
                    </Chip>
                  )}
                  {catchReport.conditions.waveHeight > 0 && (
                    <Chip icon="waves" style={styles.conditionChip}>
                      Waves: {catchReport.conditions.waveHeight} ft
                    </Chip>
                  )}
                </View>
              </>
            )}
          </Card.Content>
        </Card>
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  card: {
    margin: 16,
    marginBottom: 8,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  headerText: {
    marginLeft: 12,
    flex: 1,
  },
  details: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  chip: {
    marginRight: 8,
    marginBottom: 8,
  },
  divider: {
    marginVertical: 12,
  },
  notes: {
    fontStyle: 'italic',
  },
  segmentedButtons: {
    marginTop: 12,
  },
  mapContainer: {
    height: 300,
    marginTop: 12,
    borderRadius: 8,
    overflow: 'hidden',
    position: 'relative',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255,255,255,0.9)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  noDataOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255,255,255,0.95)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  noDataTitle: {
    marginTop: 12,
    fontSize: 18,
    textAlign: 'center',
  },
  noDataText: {
    marginTop: 8,
    textAlign: 'center',
    opacity: 0.7,
  },
  dataCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 20,
    marginTop: 12,
    borderRadius: 12,
    elevation: 2,
  },
  dataText: {
    marginLeft: 16,
    flex: 1,
  },
  dataLabel: {
    opacity: 0.7,
  },
  dataValue: {
    fontSize: 24,
  },
  conditionsTitle: {
    fontSize: 18,
    marginTop: 8,
    marginBottom: 8,
  },
  conditions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  conditionChip: {
    marginRight: 8,
    marginBottom: 8,
  },
  timestamp: {
    opacity: 0.7,
    fontSize: 12,
  },
  overlayButtonsContainer: {
    marginTop: 12,
  },
  overlayButtonRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginBottom: 8,
  },
  overlayButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.1)',
    minWidth: 70,
    alignItems: 'center',
  },
  overlayButtonText: {
    fontSize: 12,
    fontWeight: '500',
  },
  overlayButtonDisabled: {
    opacity: 0.4,
    backgroundColor: 'rgba(0,0,0,0.05)',
  },
  overlayButtonTextDisabled: {
    opacity: 0.5,
  },
  gradientNote: {
    fontSize: 12,
    fontStyle: 'italic',
    opacity: 0.7,
    marginTop: 4,
  },
});